import { motion } from 'framer-motion';
import { Palette, Users, Share2, Sparkles } from 'lucide-react';
import { displayFont, fadeInUp, staggerContainer } from './shared';
import { CDN_BASE } from '../../config/cdn';
import type { HomepageStrings } from '../../i18n/homepage';

type HowItWorksStrings = Pick<
  HomepageStrings,
  | 'how_it_works_badge'
  | 'how_it_works_h2_pre'
  | 'how_it_works_h2_highlight'
  | 'how_it_works_h2_post'
  | 'how_it_works_body'
  | 'how_it_works_steps'
>;

const EN_DEFAULTS: HowItWorksStrings = {
  how_it_works_badge: 'How It Works',
  how_it_works_h2_pre: 'Ready in ',
  how_it_works_h2_highlight: 'minutes',
  how_it_works_h2_post: ', not months',
  how_it_works_body: 'From browsing to sending, your wedding invitations are just four simple steps away.',
  how_it_works_steps: [
    { title: 'Pick your template', description: 'Browse our gallery and choose a design that feels like you.' },
    { title: 'Add your details', description: 'Our wizard walks you through names, dates, venues, and more. Done in minutes.' },
    { title: 'Add your guests', description: 'Import or add your guest list. Tag evening-only guests so they see the right info.' },
    { title: 'Share & track', description: 'Send your invite link and watch RSVPs roll in on your dashboard.' },
  ],
};

const STEP_ICONS = [Palette, Sparkles, Users, Share2];
const STEP_NUMBERS = ['01', '02', '03', '04'];

export function HowItWorksSection({ strings }: { strings?: HowItWorksStrings }) {
  const s = strings ?? EN_DEFAULTS;

  return (
    <section id="how-it-works" className="py-12 md:py-32 bg-background relative overflow-hidden">
      {/* Subtle wedding image accent */}
      <div
        className="absolute right-0 top-0 w-1/3 h-full opacity-[0.03] hidden lg:block"
        style={{
          backgroundImage: `url(${CDN_BASE}/images/placeholders/wedding-3.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4"
          >
            {s.how_it_works_badge}
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4"
            style={displayFont}
          >
            {s.how_it_works_h2_pre}<span className="text-primary">{s.how_it_works_h2_highlight}</span>{s.how_it_works_h2_post}
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-sm sm:text-base md:text-lg text-text-secondary max-w-2xl mx-auto"
          >
            {s.how_it_works_body}
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {s.how_it_works_steps.map((step, index) => {
            const Icon = STEP_ICONS[index];
            return (
              <motion.div key={index} variants={fadeInUp} className="relative h-full">
                <div className="relative bg-background-card rounded-2xl p-6 border border-border h-full flex flex-col">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-border">{STEP_NUMBERS[index]}</span>
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold text-text-primary mb-2">{step.title}</h3>
                  <p className="text-sm sm:text-base text-text-secondary flex-grow">{step.description}</p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
